import { useMemo, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { Tile, InlineLoading, InlineNotification } from '@carbon/react';
import { createResourceAtom } from 'lib/atoms';
import { DocumentResponse } from 'types/api-responses';
import { V2ResultsView } from 'components/title-chain-validator/v2/v2-results-view-wrapper';
import { TitleChainValidatorError } from 'components/title-chain-validator/title-chain-validator-error';
import { useTitleValidation } from 'lib/hooks/useTitleValidation';
import { parseLocalDateTime } from 'utils/dates';
import { fetchEstablishmentsAtom } from 'atoms/establishments-atoms';
import styles from './page.module.scss';

function ValidatorSkeleton({ message }: { message?: string }) {
  return (
    <div className={styles.v2FullPage}>
      <div className={styles.v2Loading}>
        <div className={styles.v2LoadingInner}>
          <InlineLoading description={message || 'Loading...'} />
        </div>
      </div>
    </div>
  );
}

function ValidatorForDocument({ documentId }: { documentId: number }) {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  // Remove Content padding for full-page mode
  useEffect(() => {
    const mainContent = window.document.getElementById('main-content');
    if (mainContent) {
      const originalPadding = mainContent.style.padding;
      mainContent.style.padding = '0';
      return () => {
        mainContent.style.padding = originalPadding;
      };
    }
    return undefined;
  }, []);

  const documentHref = `/documents/${documentId}`;

  const documentAtom = useMemo(
    () =>
      createResourceAtom<DocumentResponse>(
        documentHref,
        'keep-old-data-on-refetch'
      ),
    [documentHref]
  );
  const document = useAtomValue(documentAtom);

  if (document.loading) {
    return <ValidatorSkeleton message="Loading document..." />;
  }

  const docData = document.data;

  if (!docData || 'error' in docData) {
    return (
      <Tile className={styles.errorTile}>
        <InlineNotification
          kind="error"
          title="Document not found"
          subtitle={`Could not load document ${documentId}. It may not exist or you may not have access.`}
          hideCloseButton
        />
      </Tile>
    );
  }

  const titleValidatorUrl = docData.resources?.validateTitleChain?.href || '';

  return (
    <V2FullPage
      documentId={documentId}
      documentHref={docData.href}
      titleValidatorUrl={titleValidatorUrl}
      documentTitle={docData.documentTitle || ''}
      searchParams={searchParams}
    />
  );
}

function V2FullPage({
  documentId,
  documentHref,
  titleValidatorUrl,
  documentTitle,
  searchParams,
}: {
  documentId: number;
  documentHref: string;
  titleValidatorUrl: string;
  documentTitle: string;
  searchParams: URLSearchParams;
}) {
  const autoTriggeredRef = useRef(false);

  const targetEntityId = searchParams.get('target_entity_id') || '';
  const coverageThreshold = searchParams.get('coverage_threshold') || '1';
  const cutoffDate = searchParams.get('cutoff_date') || '';
  const selectedEstablishmentCount = searchParams.getAll(
    'establishment_ids[]'
  ).length;

  // Fetch establishments (atoms are consumed directly by the context panel)
  const fetchEstablishments = useSetAtom(fetchEstablishmentsAtom);

  useEffect(() => {
    if (documentId) {
      fetchEstablishments({ documentId });
    }
  }, [documentId, fetchEstablishments]);

  const {
    loading,
    ownershipGraph,
    error,
    progress,
    setTimestamp,
    triggerValidation,
    validationId,
  } = useTitleValidation(titleValidatorUrl, documentId);

  // Auto-trigger validation on mount
  useEffect(() => {
    if (autoTriggeredRef.current || !targetEntityId || !titleValidatorUrl)
      return;

    autoTriggeredRef.current = true;
    const establishmentIds = searchParams
      .getAll('establishment_ids[]')
      .map(Number);
    const formattedDate = parseLocalDateTime(new Date(), true);
    setTimestamp(formattedDate);

    triggerValidation({
      target_entity_id: targetEntityId,
      coverage_threshold: parseFloat(coverageThreshold) / 100,
      establishment_ids: establishmentIds.length ? establishmentIds : [],
      ...(cutoffDate ? { cutoff_date: cutoffDate } : {}),
    });
  }, [
    targetEntityId,
    titleValidatorUrl,
    searchParams,
    coverageThreshold,
    cutoffDate,
    setTimestamp,
    triggerValidation,
  ]);

  // Loading state
  if (loading && !ownershipGraph) {
    return (
      <ValidatorSkeleton
        message={progress?.step_label || 'Running V2 validation...'}
      />
    );
  }

  // Error state (no results to show)
  if (error && !ownershipGraph) {
    return (
      <div className={styles.v2FullPage}>
        <TitleChainValidatorError error={error} />
      </div>
    );
  }

  let reconcilerUrl: string | undefined;
  if (targetEntityId) {
    const reconcilerParams = new URLSearchParams({
      entity_id: targetEntityId,
      document_id: String(documentId),
    });
    if (validationId) reconcilerParams.set('validation_id', validationId);
    reconcilerUrl = `/title_validator/reconciler?${reconcilerParams}`;
  }

  return (
    <div className={styles.v2FullPage}>
      <V2ResultsView
        ownershipGraph={ownershipGraph}
        documentTitle={documentTitle}
        documentId={documentId}
        documentHref={documentHref}
        reconcilerUrl={reconcilerUrl}
        selectedEstablishmentCount={selectedEstablishmentCount}
      />
    </div>
  );
}

export function TitleValidatorsPage() {
  const { documentId: documentIdParam } = useParams<{ documentId: string }>();
  const documentId = Number(documentIdParam);

  if (!documentId || isNaN(documentId)) {
    return (
      <div className={styles.page}>
        <Tile className={styles.errorTile}>
          <InlineNotification
            kind="error"
            title="Missing document"
            subtitle="A document ID is required to use the title validator."
            hideCloseButton
          />
        </Tile>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <ValidatorForDocument documentId={documentId} />
    </div>
  );
}
